/**
 * The pages a team member may choose as their landing page — and, for each one, the
 * ADR-036 authorities the router lets in.
 *
 * ## Why this file exists (finding F13)
 *
 * The landing page used to be described by three lists that had to agree and nothing
 * made them: `HOME_PAGE_ROUTES` + `ROLE_DEFAULTS` in `HomeRedirect`, the `roles` column
 * of `HOME_PAGE_OPTIONS` in `PreferencesSection`, and the `allowedRoles` literals on the
 * routes themselves. When they disagreed the member was not merely sent to the wrong
 * page — they were locked out of the product: `RoleGuard` denies with
 * `<Navigate to="/" />`, `/` is `HomeRedirect`, and `HomeRedirect` sent them straight
 * back to the page they had just been denied. `/` → `/calendar` → `/` → `/calendar` → …
 *
 * So the guards for these eleven routes are declared HERE and the router reads them
 * from here; `HomeRedirect` asks the same table whether a stored preference is still
 * reachable. There is one list, not three.
 *
 * What the settings dropdown OFFERS is a second, deliberately narrower list —
 * `HOME_PAGE_OPTIONS` below. It lives here too, next to the reachability table, so the
 * difference between "may enter" and "is offered" is visible in one place rather than
 * discovered when they disagree. Selectable ⊆ reachable, pinned by
 * `homeRedirectLoop.test.tsx`, so the dropdown can never offer a page that bounces.
 *
 * ## ADR-036 (slice 3)
 *
 * The names in these lists are the six authorities of `shared/access/accessModel`, not
 * the old nine-value role enum. `/finance` is gated on the **`CAN_OPEN_FINANCE` switch**
 * rather than on a level, which is the whole point of axis B: an Admin with the switch
 * off is denied and an Admin with it on is admitted, and neither is a different level.
 */

import { AUTH, authoritiesFor, type AccessLevel } from '@/shared/access/accessModel'
import { normalizeAuthority, hasAnyAuthority } from '@/shared/access/accessModel'

/** A landing page's route path and the authorities the router admits (null = every member). */
export interface HomePageRoute {
  path: string
  allowedRoles: string[] | null
}

export const HOME_PAGE_ROUTES = {
  // Open to EVERY level since 2026-09-08 (DESIGN-what-is-crew-for.md, Q2 finding 1;
  // Andras: "I agree with the recommendation build accordingly, go"). `null` here is
  // the same declaration `/attention`, `/chat` and `/team` already carry: the page
  // holds nothing a level gates. Its widgets gate THEMSELVES on the authorities their
  // own endpoints admit — `WIDGET_ACCESS` in DashboardPage — so the money half stays
  // exactly where it was and the person-addressed half stops being unreachable.
  DASHBOARD:    { path: '/dashboard',    allowedRoles: null },
  SHOOTS:       { path: '/shoots',       allowedRoles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW] },
  // Finance reads the task board: `/api/v1/tasks` admits BOOKKEEPER, and a bookkeeper's
  // own chase-ups live there.
  TASKS:        { path: '/tasks',        allowedRoles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW, AUTH.BOOKKEEPER] },
  // ADR-036 axis A: Crew see projects — all of them, or only their own when the
  // `SCOPE_ASSIGNED_ONLY` switch is on. The switch is applied by the SERVER's scoping
  // query, never by hiding the route: a scoped Crew opens the same page and it holds
  // the projects they are on. Finance gets names and titles only, same page.
  PROJECTS:     { path: '/projects',     allowedRoles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW, AUTH.BOOKKEEPER] },
  CALENDAR:     { path: '/calendar',     allowedRoles: [AUTH.OWNER, AUTH.ADMIN, AUTH.CREW] },
  INVOICES:     { path: '/invoices',     allowedRoles: [AUTH.OWNER, AUTH.ADMIN, AUTH.BOOKKEEPER] },
  MY_PROJECTS:  { path: '/my-projects',  allowedRoles: [AUTH.CREW] },
  // The crew who shoot the job upload and manage it here too (F73/F81). `/api/v1/my`
  // is `hasRole('CREW')` and lists only their own assignments.
  MY_GALLERIES: { path: '/my-galleries', allowedRoles: [AUTH.CREW] },
  MY_TASKS:     { path: '/my-tasks',     allowedRoles: [AUTH.CREW] },
  // The money boundary, and the ONLY route gated on a switch rather than a level.
  // `hasAuthority('CAN_OPEN_FINANCE')` is what every /api/v1/finance, /reports and
  // /expenses endpoint asks, so the page and its data agree by construction.
  FINANCE:      { path: '/finance',      allowedRoles: [AUTH.CAN_OPEN_FINANCE] },
  // Chat carries no RoleGuard — every authenticated member of the studio is in it.
  CHAT:         { path: '/chat',         allowedRoles: null },
} satisfies Record<string, HomePageRoute>

export type HomePageValue = keyof typeof HOME_PAGE_ROUTES

/**
 * Where a member with no stored preference lands, keyed by ADR-036 access level.
 * This is the same fact the settings dropdown labels as "Default (…)", so both read
 * it from here.
 *
 * Every entry must name a page that level can enter — pinned by
 * `homeRedirectLoop.test.tsx`, because a role default that bounces is the loop again.
 * Note that the check there uses the level's FULL authority set (`authoritiesFor`),
 * which is why BOOKKEEPER → `/finance` is legal: the level always carries the
 * `CAN_OPEN_FINANCE` switch, and the route is gated on the switch.
 */
export const ROLE_DEFAULT_HOME: Record<AccessLevel, HomePageValue> = {
  OWNER:   'DASHBOARD',
  ADMIN:   'DASHBOARD',
  // Crew land on the Dashboard too, now that it admits them (2026-09-08). It is the
  // one page that answers "what is addressed to me" — the attention items raised for
  // them, their own tasks, their shoots — and landing them on `/my-projects` instead
  // is what left an urgent `@!` mention sitting unseen. `MY_PROJECTS` is still offered
  // in the dropdown for anyone who wants it.
  CREW:    'DASHBOARD',
  BOOKKEEPER: 'FINANCE',
}

/**
 * What the settings dropdown OFFERS, per level.
 *
 * Deliberately NARROWER than `HOME_PAGE_ROUTES` above, and that is a product choice
 * rather than a second copy of the matrix: Crew may enter `/projects` but navigate by
 * their own assignments and are offered the `MY_*` pages instead, and Finance may
 * enter `/projects` for names and titles without that being a sensible place to land.
 *
 * The constraint runs one way only: everything offered here MUST be reachable per
 * `HOME_PAGE_ROUTES`, or the member picks a page `RoleGuard` then throws them out of —
 * a sign-in loop, not a wrong landing page. `homeRedirectLoop.test.tsx` pins the subset.
 */
export const HOME_PAGE_OPTIONS: { value: string; roles: AccessLevel[] }[] = [
  { value: 'DASHBOARD',    roles: ['OWNER', 'ADMIN', 'CREW', 'BOOKKEEPER'] },
  { value: 'PROJECTS',     roles: ['OWNER', 'ADMIN'] },
  { value: 'MY_PROJECTS',  roles: ['CREW'] },
  { value: 'MY_GALLERIES', roles: ['CREW'] },
  { value: 'SHOOTS',       roles: ['OWNER', 'ADMIN', 'CREW'] },
  { value: 'CALENDAR',     roles: ['OWNER', 'ADMIN', 'CREW'] },
  { value: 'TASKS',        roles: ['OWNER', 'ADMIN', 'BOOKKEEPER'] },
  { value: 'MY_TASKS',     roles: ['CREW'] },
  { value: 'INVOICES',     roles: ['OWNER', 'ADMIN', 'BOOKKEEPER'] },
  // Offered to the two levels that always hold the switch. An Admin who has it turned
  // on may still ENTER /finance; they are simply not offered it as a landing page,
  // because the switch can be turned off again and the preference would then orphan.
  { value: 'FINANCE',      roles: ['OWNER', 'BOOKKEEPER'] },
  { value: 'CHAT',         roles: ['OWNER', 'ADMIN', 'CREW', 'BOOKKEEPER'] },
]

/** The home-page options to show a member holding these authorities. */
export function getAvailableHomePageOptions(userRoles: string[]): typeof HOME_PAGE_OPTIONS {
  const mine = new Set(userRoles.map(normalizeAuthority))
  return HOME_PAGE_OPTIONS.filter((opt) => opt.roles.some((r) => mine.has(r)))
}

/**
 * The terminus of `resolveHomeTarget`. `/settings` carries no `RoleGuard`, so it is
 * reachable by every authenticated member — which is the whole point: it makes the
 * resolution a total function and the redirect loop structurally impossible rather
 * than merely unlikely. Do not point this at a guarded route.
 */
export const UNIVERSAL_FALLBACK_PATH = '/settings'

export function canEnter(allowedRoles: string[] | null, roles: string[]): boolean {
  return hasAnyAuthority(roles, allowedRoles)
}

/** The page a member with no stored preference should land on. */
export function roleDefaultHomePage(roles: string[]): HomePageValue {
  for (const role of roles) {
    const fromLevel = ROLE_DEFAULT_HOME[normalizeAuthority(role) as AccessLevel]
    if (fromLevel) return fromLevel
  }
  return 'DASHBOARD'
}

/**
 * The path to send a member to from `/`, guaranteed to be one their CURRENT authorities
 * can enter.
 *
 * The stored preference is a hint, not an instruction: it may name a page that never
 * existed (nothing validated this field before F13), or one that was fine until an
 * owner changed the member's access — a Crew member who chose `MY_PROJECTS` and was
 * later made Finance, or an Admin whose "sees finances" switch was turned off while
 * `FINANCE` was their landing page. Either way the stored value is skipped rather than
 * obeyed, so the row heals itself on the next sign-in instead of locking the member out.
 */
export function resolveHomeTarget(roles: string[], storedHomePage: string | null | undefined): string {
  const candidates: (string | null | undefined)[] = [storedHomePage, roleDefaultHomePage(roles)]
  for (const candidate of candidates) {
    const route = candidate ? HOME_PAGE_ROUTES[candidate as HomePageValue] : undefined
    if (route && canEnter(route.allowedRoles, roles)) return route.path
  }
  return UNIVERSAL_FALLBACK_PATH
}

export { authoritiesFor }
